/**
 * Los archivos de imagen que cada marca publica: logo y fondos.
 *
 * Van aparte de las primitivas de color porque el modo de falla es distinto.
 * Un color que falta se hereda y pinta el tono de otra marca — sutil, hay que
 * mirarlo con atención. Un logo que falta muestra **la marca equivocada**: un
 * cliente que abre su app con su tema activo ve el logotipo de Silia.
 *
 * ── Estado ────────────────────────────────────────────────────────────────
 *
 * Traxión NO tiene sus archivos en el repo. `public/assets/` solo trae los de
 * Silia (más los de eca/lipu/neutral, marcas discontinuadas). Así que sus
 * entradas apuntan a los de Silia y están marcadas `placeholder: true`.
 *
 * Eso es deliberado y es el punto de este archivo: convierte una herencia
 * silenciosa en una declaración explícita que un test vigila. El día que
 * lleguen los archivos, esto es cambiar una ruta y borrar el flag —
 * `brandAssets.test.ts` se pone en verde solo.
 */

import {
  silliaLogo,
  silliaLogoMobile,
  silliaBackground,
  silliaBackgroundWebp,
  silliaHeroBackground,
  silliaHeroBackgroundWebp,
  silliaHeroBackgroundWebpMobile,
  silliaModulesBackground,
  silliaModulesBackgroundWebp,
} from '../primitives';

/**
 * Rutas crudas, sin envolver en `url()`.
 *
 * Se guardan crudas porque el mismo archivo se consume de dos formas: el CSS
 * necesita `url(...)` y `useTheme()` lee la ruta pelada con `getComputedStyle`
 * para pasarla a un `<img src>`. Envolver acá obligaría a desenvolver allá.
 */
export type BrandAssets = {
  logo: string;
  logoMobile: string;
  background: string;
  backgroundWebp: string;
  heroBackground: string;
  heroBackgroundWebp: string;
  heroBackgroundWebpMobile: string;
  modulesBackground: string;
  modulesBackgroundWebp: string;
};

/**
 * Una marca y sus archivos.
 *
 * `placeholder` no es una nota al pie: es lo que hace que la deuda sea
 * contable. Sin ese flag, "Traxión usa el logo de Silia" es una frase en un
 * comentario que nadie relee; con él, es un booleano que un test cuenta.
 */
export type BrandAssetSet = {
  assets: BrandAssets;
  /** true mientras los archivos sean prestados de otra marca. */
  placeholder: boolean;
};

export const siliaAssets: BrandAssetSet = {
  placeholder: false,
  assets: {
    logo: silliaLogo,
    logoMobile: silliaLogoMobile,
    background: silliaBackground,
    backgroundWebp: silliaBackgroundWebp,
    heroBackground: silliaHeroBackground,
    heroBackgroundWebp: silliaHeroBackgroundWebp,
    heroBackgroundWebpMobile: silliaHeroBackgroundWebpMobile,
    modulesBackground: silliaModulesBackground,
    modulesBackgroundWebp: silliaModulesBackgroundWebp,
  },
};

/**
 * TODO(assets-traxion): reemplazar por los archivos de Traxión y poner
 * `placeholder: false`. Mientras esto siga así, un usuario con Traxión activo
 * ve el logotipo de Silia en el sidebar y en las pantallas de login,
 * activación y reset de contraseña.
 */
export const traxionAssets: BrandAssetSet = {
  placeholder: true,
  assets: { ...siliaAssets.assets },
};

/** Envuelve una ruta para consumirla desde CSS. */
export const asCssUrl = (path: string): string => `url(${path})`;

/**
 * Los tokens `--ant-*` de asset que una marca tiene que declarar.
 *
 * La lista es explícita, y no derivada de `BrandAssets`, porque los nombres no
 * se corresponden uno a uno: `heroBackground` produce cinco tokens (dos `Url`
 * para CSS y tres `Src` que lee `useTheme`), y `logo` produce un `Url` pero
 * también un `logoSrc` que no lleva prefijo de marca.
 */
export const assetVarsFor = (set: BrandAssetSet): Record<string, string> => {
  const a = set.assets;
  return {
    '--ant-logoUrl': asCssUrl(a.logo),
    '--ant-logoMobileUrl': asCssUrl(a.logoMobile),
    '--ant-backgroundUrl': asCssUrl(a.background),
    '--ant-backgroundUrlWebp': asCssUrl(a.backgroundWebp),
    '--ant-heroBackgroundUrl': asCssUrl(a.heroBackground),
    '--ant-heroBackgroundUrlWebp': asCssUrl(a.heroBackgroundWebp),
    // Los tres `Src` van crudos: `useTheme()` los lee con getComputedStyle y
    // los pasa a un <img src>, donde `url(...)` no sirve.
    '--ant-heroBackgroundSrc': a.heroBackground,
    '--ant-heroBackgroundSrcWebp': a.heroBackgroundWebp,
    '--ant-heroBackgroundSrcWebpMobile': a.heroBackgroundWebpMobile,
    '--ant-modulesBackgroundUrl': asCssUrl(a.modulesBackground),
    '--ant-modulesBackgroundUrlWebp': asCssUrl(a.modulesBackgroundWebp),
  };
};

/** Emite las declaraciones CSS de una marca, listas para inyectar. */
export const emitAssetVars = (set: BrandAssetSet, indent = '    '): string =>
  Object.entries(assetVarsFor(set))
    .map(([name, value]) => `${indent}${name}: ${value};`)
    .join('\n');
